import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import usersApi, { User, CreateUserRequest, UpdateUserRequest, UserRole } from '@/services/apis/users';

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: any) => [...userKeys.lists(), filters] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
};

export interface UsersFilters {
  limit?: number;
  departmentId?: string;
  role?: UserRole;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Hook for fetching users with infinite scroll
export const useInfiniteUsers = (filters: UsersFilters = {}) => {
  const { limit = 10, departmentId, role, search, sortBy = 'firstName', sortOrder = 'asc' } = filters;

  return useInfiniteQuery({
    queryKey: userKeys.list({ limit, departmentId, role, search, sortBy, sortOrder }),
    queryFn: async ({ pageParam }) => {
      const response = await usersApi.getUsers({
        cursor: pageParam,
        limit,
        departmentId,
        role,
        search,
        sortBy,
        sortOrder,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useUser = ({ id, shouldSkip }: { id: string; shouldSkip: boolean }) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: async () => {
      const response = await usersApi.getUser(id);
      return response.data!;
    },
    enabled: !shouldSkip && !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserRequest) => {
      const response = await usersApi.createUser(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserRequest }) => {
      const response = await usersApi.updateUser(id, data);
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(data.data?.id || '') });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await usersApi.deleteUser(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useSyncFromSlack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await usersApi.syncFromSlack();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};
